<template>
  <div class="d-flex flex-column-fluid zand-payment-page">
    <div class="container">
      
      <!-- Page Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="header-title">
            <h1 class="title">Payment Details</h1>
            <p class="subtitle">Review and confirm payment details</p>
          </div>
          <div class="header-actions">
            <button class="btn btn-dark btn-dashboard" @click="$router.go(-1)">
              <i class="flaticon2-line-chart"></i>
              Back to Requests
            </button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="spinner-border" role="status"></div>
        <div class="loading-text">Loading payment details...</div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <div class="alert alert-danger">
          <i class="material-icons">error</i>
          {{ error }}
          <div class="mt-3">
            <button class="btn btn-secondary" @click="$router.go(-1)">Go Back</button>
          </div>
        </div>
      </div>

      <!-- Payment Details -->
      <div v-else-if="financeRequest" class="payment-content">

      <!-- Payment Summary Card -->
      <div class="payment-summary-card">
        <div class="card-header">
          <h5 class="card-title">
            <i class="material-icons text-success">account_balance</i>
            Payment Summary
          </h5>
        </div>
        <div class="card-body">
          <div class="summary-grid">
            <div class="summary-item">
              <label>Reference Number</label>
              <div class="value">{{ financeRequest.invoice_reference }}</div>
            </div>
            <div class="summary-item">
              <label>Payment Amount</label>
              <div class="value amount">{{ formatCurrency(financeRequest.disbursement_amount, financeRequest.currency || currency) }}</div>
            </div>
            <div class="summary-item">
              <label>Recipient Name</label>
              <div class="value">{{ getBankDetails().account_holder_name || 'N/A' }}</div>
            </div>
            <div class="summary-item">
              <label>Recipient IBAN</label>
              <div class="value iban">{{ getBankDetails().account_iban || 'N/A' }}</div>
            </div>
            <div class="summary-item">
              <label>Bank</label>
              <div class="value">Zand Bank</div>
            </div>
            <div class="summary-item">
              <label>Payment Method</label>
              <div class="value">Bank Transfer</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Business Details Card -->
      <div class="business-details-card">
        <div class="card-header">
          <h5 class="card-title">
            <i class="material-icons text-info">business</i>
            Business Information
          </h5>
        </div>
        <div class="card-body">
          <div class="business-grid">
            <div class="business-item">
              <label>Business Name</label>
              <div class="value">{{ financeRequest.borrower_name || financeRequest.business_name || 'N/A' }}</div>
            </div>
            <div class="business-item">
              <label>Request Status</label>
              <span :class="['status-badge', statusBadgeClass(financeRequest.status)]">
                {{ formatStatusLabel(financeRequest.status) }}
              </span>
            </div>
            <div class="business-item">
              <label>Total Receivable</label>
              <div class="value">{{ formatCurrency(financeRequest.total_receivable_amount, financeRequest.currency || currency) }}</div>
            </div>
            <div class="business-item">
              <label>Transaction Fee</label>
              <div class="value">{{ formatCurrency(financeRequest.transaction_fee, financeRequest.currency || currency) }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Invoice Details Card -->
      <div class="invoice-details-card">
        <div class="card-header">
          <h5 class="card-title">
            <i class="material-icons text-warning">receipt</i>
            Invoice Details
            <span class="invoice-count">({{ financeRequest.invoices ? financeRequest.invoices.length : 0 }} {{ financeRequest.invoices && financeRequest.invoices.length === 1 ? 'invoice' : 'invoices' }})</span>
          </h5>
        </div>
        <div class="card-body">
          <div class="table-responsive">
            <table class="table table-bordered">
              <thead>
                <tr>
                  <th>Invoice Reference</th>
                  <th class="text-right">Invoice Amount</th>
                  <th class="text-right">Funded Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="invoice in financeRequest.invoices" :key="invoice.id">
                  <td>{{ invoice.invoice_reference }}</td>
                  <td class="text-right">{{ formatCurrency(invoice.invoice_amount, financeRequest.currency || currency) }}</td>
                  <td class="text-right">{{ formatCurrency(invoice.funded_amount, financeRequest.currency || currency) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Payment Status Messages -->
      <div v-if="paymentSuccess" class="alert alert-success payment-status-message">
        <i class="material-icons">check_circle</i>
        <div class="status-content">
          <h6>Payment Initiated Successfully!</h6>
          <p>Your payment has been successfully initiated through Zand Banking.</p>
        </div>
      </div>

      <div v-if="paymentError" class="alert alert-danger payment-status-message">
        <i class="material-icons">error</i>
        <div class="status-content">
          <h6>Payment Failed</h6>
          <p>{{ paymentError }}</p>
        </div>
      </div>

      <!-- Payment Actions -->
      <div class="payment-actions">
        <div v-if="!paymentSuccess" class="warning-notice">
          <i class="material-icons text-warning">warning</i>
          <div class="warning-text">
            <strong>Important:</strong> This payment will be processed immediately through Zand Banking. 
            Please ensure all details are correct before proceeding.
          </div>
        </div>
        
        <div class="action-buttons">
          <button 
            type="button" 
            class="btn btn-secondary btn-lg"
            @click="$router.go(-1)"
            :disabled="initiatingPayment"
          >
            <i class="material-icons">close</i>
            Cancel
          </button>
          
          <button 
            type="button" 
            class="btn btn-success btn-lg"
            @click="initiatePayment"
            :disabled="initiatingPayment || paymentSuccess"
          >
            <span v-if="initiatingPayment" class="spinner-border spinner-border-sm mr-2"></span>
            <i v-else-if="paymentSuccess" class="material-icons">check_circle</i>
            <i v-else class="material-icons">send</i>
            {{ paymentSuccess ? 'Payment Completed' : (initiatingPayment ? 'Processing Payment...' : 'Send Payment') }}
          </button>
        </div>
      </div>
      
    </div>
    </div>
  </div>
</template>

<script>
import FinancingService from '@/services/financing.service';

export default {
  name: 'ZandPayment',
  props: {
    currency: {
      type: String,
      default: 'USD'
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      financeRequest: null,
      initiatingPayment: false,
      paymentSuccess: false,
      paymentError: null,
    };
  },
  async mounted() {
    await this.loadFinanceRequest();
  },
  methods: {
    async loadFinanceRequest() {
      try {
        this.loading = true;
        this.error = null;
        
        const requestId = this.$route.params.requestId;
        if (!requestId) {
          throw new Error('Finance request ID not provided');
        }
        
        const response = await FinancingService.getFinanceRequest(requestId);
        this.financeRequest = response.data;
        
        // Validate that this is an approved request
        if (this.financeRequest.status !== 'APPROVED') {
          throw new Error('This finance request is not approved for payment');
        }

        const businessId = this.financeRequest.borrower_id || this.financeRequest.business_id;
        if (businessId) {
          const financingResponse = await FinancingService.getFinancingForBusiness(businessId);
          const creditLine = FinancingService.unwrapEntity(financingResponse);
          if (FinancingService.isCreditLineAwaitingApproval(creditLine && creditLine.status)) {
            throw new Error('Credit line is awaiting approval. Disbursement is unavailable until it is approved.');
          }
        }
        
      } catch (error) {
        console.error('Error loading finance request:', error);
        this.error = error.message || 'Failed to load finance request details. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    
    async initiatePayment() {
      try {
        this.initiatingPayment = true;
        this.paymentError = null;
        
        const response = await FinancingService.initiateZandPayment(this.financeRequest.id);
        
        console.log('Zand Payment Response:', response); // Debug log to see actual response structure
        
        // Check for success in multiple possible response formats
        const isSuccess = response && (
          response.success === true || 
          response.status === 'success' || 
          response.status === 'completed' ||
          response.success === 'true' ||
          // If response exists and doesn't have explicit error indicators, consider it successful
          (response && !response.error && !response.message?.toLowerCase().includes('error') && !response.message?.toLowerCase().includes('fail'))
        );
        
        if (isSuccess) {
          this.paymentSuccess = true;
          this.$showAlert('Payment initiated successfully.', 'success');
          
          // Redirect back to financing requests after a short delay
          setTimeout(() => {
            this.$router.push({
              name: 'financing-requests',
              params: { business_id: this.financeRequest.borrower_id || this.financeRequest.business_id }
            });
          }, 3000);
          
        } else {
          // Extract error message from various possible response formats
          const errorMessage = response?.message || response?.error || response?.detail || 'Payment initiation failed';
          throw new Error(errorMessage);
        }
      } catch (error) {
        console.error('Error initiating payment:', error);
        const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to initiate payment. Please try again.';
        this.paymentError = errorMessage;
        this.$showAlert(errorMessage, 'danger');
      } finally {
        this.initiatingPayment = false;
      }
    },

    getBankDetails() {
      if (!this.financeRequest || !this.financeRequest.bank_details) {
        return { account_holder_name: 'N/A', account_iban: 'N/A' };
      }
      try {
        const bankDetails = typeof this.financeRequest.bank_details === 'string' 
          ? JSON.parse(this.financeRequest.bank_details) 
          : this.financeRequest.bank_details;
        return {
          account_holder_name: bankDetails.account_holder_name || 'N/A',
          account_iban: bankDetails.account_iban || 'N/A'
        };
      } catch (error) {
        console.error('Error parsing bank details:', error);
        return { account_holder_name: 'N/A', account_iban: 'N/A' };
      }
    },

    formatCurrency(amount, currency) {
      if (amount === undefined || amount === null || isNaN(amount)) {
        return Number(0).toLocaleString(undefined, { 
          style: 'currency', 
          currency: currency || 'USD', 
          minimumFractionDigits: 2 
        });
      }
      return Number(amount).toLocaleString(undefined, { 
        style: 'currency', 
        currency: currency || 'USD', 
        minimumFractionDigits: 2 
      });
    },

    formatStatusLabel(status) {
      const statusLabels = {
        'PENDING_REVIEW': 'Pending Review',
        'AWAITING_APPROVAL': 'Awaiting approval',
        'APPROVED': 'Approved', 
        'REJECTED': 'Rejected',
        'DISBURSED_AMOUNT': 'Disbursed Amount',
        "CLOSED": "Closed",
        "PARTIALLY_PAID": "Partially Paid",
      };
      return statusLabels[status] || status;
    },

    statusBadgeClass(status) {
      if (!status) return 'status-default';
      const s = status.toLowerCase();
      if (s === 'approved' || s === 'funded') return 'status-approved';
      if (s === 'pending') return 'status-pending';
      if (s === 'rejected') return 'status-rejected';
      if (s === 'processing') return 'status-processing';
      if (s === 'pending_review') return 'status-pending_review';
      if (s === 'disbursed_amount') return 'status-disbursed_amount';
      if (s === 'closed') return 'status-closed';
      return 'status-' + s.replace(/[^a-z0-9_-]/g, '') || 'status-default';
    }
  }
};
</script>

<style lang="scss" scoped>
@import './ZandPayment.scss';
</style>